/**
 * Decoration Registry
 *
 * Central registry mapping decoration set names to their components.
 *
 * To add a new decoration set:
 * 1. Create a new directory under `components/decorations/sets/[set-name]/`
 * 2. Create `[SetName]Background.tsx` and optionally `[SetName]Nav.tsx`
 * 3. Add the set name to `VALID_DECORATION_SETS` in `lib/utils/decorations.ts`
 * 4. Import the components here and add them to the registry below
 */

import type { DecorationRegistry } from "@/components/decorations/types";
import { isDecorationSetName } from "@/lib/utils/decorations";
import { FallBackground } from "./sets/fall/FallBackground";
import { Halloween2025Background } from "./sets/halloween2025/Halloween2025Background";
import { Halloween2025Nav } from "./sets/halloween2025/Halloween2025Nav";
import { SummerBackground } from "./sets/summer/SummerBackground";
import { ValentinesBackground } from "./sets/valentines/ValentinesBackground";
import { WinterBackground } from "./sets/winter/WinterBackground";

/**
 * Decoration registry mapping set names to their components.
 *
 * Each entry can include:
 * - background: Background decoration component (optional)
 * - nav: Navigation decoration component (optional)
 * - logo: Site wordmark override (optional)
 * - gameBarBackground: In-game theater bar textures (optional)
 */
/**
 * The default wordmark is a 180×27 SVG shown at `h-6` (1.5rem). Seasonal
 * artboards that share that 180-wide canvas use the same scale, so a taller
 * viewBox grows in height instead of being squeezed into 1.5rem.
 */
export const HALLOWEEN_LOGO = {
  src: "/images/halloween/logo.svg",
  width: 180,
  height: 44,
  displayHeight: "calc(1.5rem * 44 / 27)",
} as const;

export const HALLOWEEN_GAME_BAR_BACKGROUND = {
  horizontal: "/images/halloween/bar-horizontal.webp",
  vertical: "/images/halloween/bar-vertical.webp",
} as const;

/** 1280×220 art, drawn at 88px so the drips are smaller than the first tile. */
export const HALLOWEEN_NAV_SLIME = {
  src: "/images/halloween/nav-slime.webp",
  height: 88,
  tuck: 16,
} as const;

export const HALLOWEEN_FOOTER_ART = {
  src: "/images/halloween/footer-witch.webp",
  width: 468,
  height: 512,
} as const;

export const decorationRegistry: DecorationRegistry = {
  halloween: {
    logo: HALLOWEEN_LOGO,
    gameBarBackground: HALLOWEEN_GAME_BAR_BACKGROUND,
    navSlime: HALLOWEEN_NAV_SLIME,
    footerArt: HALLOWEEN_FOOTER_ART,
  },
  halloween2025: {
    background: Halloween2025Background,
    nav: Halloween2025Nav,
  },
  fall: {
    background: FallBackground,
  },
  winter: {
    background: WinterBackground,
  },
  valentines: {
    background: ValentinesBackground,
  },
  summer: {
    background: SummerBackground,
  },
  // Add future decoration sets here following the same pattern:
  // "partner-promo-x": {
  //   background: PartnerPromoXBackground,
  //   nav: PartnerPromoXNav, // optional
  // },
};

/**
 * Gets decoration set components by name.
 *
 * Accepts raw string input and performs normalization (trimming, lowercasing) and validation.
 * This allows callers to pass raw input (e.g., from environment variables, config files, API responses)
 * without needing type assertions, while still providing type-safe access to decoration components.
 *
 * @param name - The decoration set name as a raw string (will be normalized and validated)
 * @returns The decoration set components, or null if name is invalid/not found
 * @example
 * ```ts
 * // Accepts raw strings
 * getDecorationSet("halloween"); // returns halloween components
 * getDecorationSet(" halloween "); // normalizes whitespace, returns halloween components
 * getDecorationSet("invalid"); // returns null
 * ```
 */
export function getDecorationSet(
  name: string | null
): DecorationRegistry[keyof DecorationRegistry] | null {
  // Defensive null check: handle null/undefined inputs first
  if (!name) {
    return null;
  }

  // Normalize the name by trimming whitespace
  // This ensures names with leading/trailing whitespace (e.g., " halloween ") are
  // normalized to their canonical form ("halloween") before lookup
  const trimmedName = name.trim();

  // Whitespace validation: check if trimmed name is empty
  // This handles empty strings ("") and whitespace-only strings ("  ", "\t", "\n")
  if (!trimmedName) {
    return null;
  }

  // Normalize to lowercase for case-insensitive matching
  const normalizedName = trimmedName.toLowerCase();

  // Validate against valid decoration sets using type guard
  if (!isDecorationSetName(normalizedName)) {
    // Log warning in development mode for invalid decoration set names
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[Decorations] Invalid decoration set name: "${name}". Valid options are: ${Object.keys(decorationRegistry).join(", ")}.`
      );
    }
    return null;
  }

  // Type-safe lookup after validation
  const set = decorationRegistry[normalizedName];

  // Additional safety check (should never happen after isDecorationSetName validation)
  if (!set) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[Decorations] Decoration set "${normalizedName}" validated but not found in registry. This indicates a bug.`
      );
    }
    return null;
  }

  return set;
}
